<template>
  <div class="d-flex-column justify-start align-start gap-8">
    <div class="d-flex-row justify-start gap-5">
      <label for="" class="t-light regular-l font-family-2">
        {{ $t("fc-boost.divisions") }}
      </label>
      <ItemTooltip :text="$t('fc-boost.descriptions.divisions')" />
    </div>
    <div
      v-if="!$isMobile() && !$isTablet()"
      class="fc-divisions-picker b-radius-6 cursor-pointer"
      @click.stop="toggleOpen"
      v-click-outside="closeOpen"
      :class="[selectedOptions <= 0 && 'errored']"
    >
      <div class="d-flex-row flex-8 justify-start">
        <p
          class="t-white font-family-2 regular-l f-w-500"
          :class="[selectedOptions <= 0 && 't-danger']"
          v-if="selectedOptions > 0 && !selectOne"
        >
          {{ selectedOptions }} {{ $t("dashboard.options") }}
        </p>

        <p
          class="t-white font-family-2 regular-l f-w-500"
          :class="[selectedOptions <= 0 && 't-danger']"
          v-if="singleSelected && selectOne"
        >
          {{ singleSelected?.title }}
        </p>
      </div>
      <ChevDown />
      <Transition name="slide-fade-top">
        <div class="divisions_list_container" v-if="isOpen">
          <div
            class="item"
            v-for="option in options"
            :key="option"
            :class="[isSelected(option) && 'active_item']"
            @click.stop="selectItem(option)"
          >
            <p class="t-white font-family-2 regular-l">{{ option.title }}</p>
            <p class="t-blue font-family-2 regular-l" v-if="!selectOne">
              {{ renderPrice(option) }}
            </p>
          </div>
        </div>
      </Transition>
    </div>
    <BottomSheetPicker
      :title="$t('fc-boost.divisions')"
      :show-title="false"
      v-else-if="($isMobile() || $isTablet()) && selectOne"
      :selected-item="singleSelected?.slab"
      :items="keyedOptions"
      :select-filter="selectMobileItem"
      filter-key="slab"
      item-key="slab"
    />
    <BottomSheetPicker
      :title="$t('fc-boost.divisions')"
      :show-title="false"
      v-else-if="($isMobile() || $isTablet()) && !selectOne"
      :is-multi-select="true"
      :selected-item="checkoutStore.getDetails?.optionSlabs"
      :items="options"
      :select-filter="selectMobileItem"
      filter-key="slab"
      item-key="slab"
      :show-multi-select-length="true"
    />
  </div>
</template>

<script setup>
import ChevDown from "~/components/icons/ChevDown.vue";
import ItemTooltip from "./ItemTooltip.vue";
import BottomSheetPicker from "~/components/common/BottomSheetPicker.vue";

const isOpen = ref(false);

const props = defineProps({
  selectOne: {
    default: false,
  },
});

const toggleOpen = () => {
  isOpen.value = !isOpen.value;
};

const closeOpen = () => {
  if (isOpen.value) {
    isOpen.value = false;
  }
};

const gameStore = useGameStore();
const checkoutStore = useCheckoutStore();
const keyedOptions = computed(() => {
  return gameStore.getGamePack?.prices
    ?.filter((price) => price.title !== "CQP")
    .reduce((accumulator, value) => {
      return { ...accumulator, [value.slab]: value.title };
    }, {});
});
const options = computed(() => {
  return gameStore.getGamePack?.prices?.filter(
    (price) => price.title !== "CQP"
  );
});

const renderPrice = (packOption) => {
  if (packOption?.type === 0) {
    return `+${parseFloat(packOption.price).toFixed(2)}$`;
  }
  if (packOption?.type === 1) {
    return `+${parseFloat(packOption.price).toFixed(2)}%`;
  }
};

const selectedOptions = computed(() => {
  const selectedSlabs = checkoutStore.getDetails?.optionSlabs || [];
  return selectedSlabs.length;
});
const singleSelected = computed(() => {
  const selectedSlabs =
    options?.value?.find((op) =>
      checkoutStore.getDetails?.optionSlabs.includes(op.slab)
    ) || [];
  return selectedSlabs;
});

const isSelected = (packOption) => {
  const selectedSlabs = checkoutStore.getDetails?.optionSlabs || [];
  return selectedSlabs?.includes(packOption?.slab);
};

const selectMobileItem = (key, value) => {
  if (props.selectOne) {
    const selectedOption = options.value.find((op) => op.slab == value);
    if (selectedOption) {
      selectItem(selectedOption);
    }
  } else {
    checkoutStore.setDetails({
      ...checkoutStore.getDetails,
      optionSlabs: [...value],
    });
  }
};

const selectItem = (packOption) => {
  if (!props.selectOne) {
    const selectedSlabs = checkoutStore.getDetails?.optionSlabs || [];
    if (selectedSlabs?.includes(packOption?.slab)) {
      const others =
        selectedSlabs?.filter((option) => option !== packOption?.slab) || [];

      checkoutStore.setDetails({
        ...checkoutStore.getDetails,
        optionSlabs: [...others],
      });
    } else {
      const others = [...selectedSlabs, packOption.slab];
      checkoutStore.setDetails({
        ...checkoutStore.getDetails,
        optionSlabs: [...others],
      });
    }
  } else if (props.selectOne) {
    const selectedSlabs = checkoutStore.getDetails?.optionSlabs || [];
    checkoutStore.setDetails({
      ...checkoutStore.getDetails,
      optionSlabs: [packOption.slab],
    });
  }
};
</script>

<style lang="scss">
.fc-divisions-picker {
  display: flex;
  height: 42px;
  padding: 0px 12px;
  justify-content: space-between;
  align-items: center;
  gap: 11px;
  flex-shrink: 0;
  align-self: stretch;
  border: 1px solid var(--stroke-medium, #27292b);
  background: var(--bg-medium, #121517);
  position: relative;
  &.errored {
    border: 1px solid var(--path-of-exile);
    background: var(--path-of-exile-opace);
  }
  .divisions_list_container {
    position: absolute;
    top: 105%;
    left: 0%;
    width: 100%;
    height: max-content;
    max-height: 30vh;
    overflow: auto;
    background-color: var(--bg-medium);
    border: 1px solid var(--stroke-medium);
    z-index: 9999;
    border-radius: 3px;
    -moz-border-radius: 3px;
    -webkit-border-radius: 3px;
    padding: 6px;
    display: flex;
    justify-content: flex-start;
    align-items: flex-start;
    flex-direction: column;
    z-index: 10;
    gap: 6px;
    .item {
      width: 100%;
      height: 48px;
      outline: none;
      border: none;
      text-align: start;
      padding: 11px;
      border-radius: 3px;
      -moz-border-radius: 3px;
      -webkit-border-radius: 3px;
      cursor: pointer;
      display: flex;
      justify-content: flex-start;
      align-items: center;
      gap: 5px;
      z-index: 1;
      @media screen and (max-width: 480px) {
        padding: 16px;
      }

      &.active_item {
        background-color: var(--bg-main);
        color: var(--white);
      }
    }
  }
}
</style>
